// gravitytype.js (gravity agent, LIGHT.md 4): the visitor's pointer has mass, and so does Sagittarius A*.
// post.js lenses the WebGL frame around the pointer; this module moves the headline letters by the same
// point-mass lens. A lens with Einstein radius tE images a source at offset beta to
//   theta+ = (beta + sqrt(beta^2 + 4 tE^2)) / 2   along the same direction,
// stretched tangentially by theta+/beta (capped at 2.5) and squeezed radially by theta^2 / (theta^2 + tE^2).
// A point lens's magnification matrix is symmetric (images are stretched, never turned), so each letter
// gets rotate(to the tangent) . scaleX(stretch) . rotate(back), written as one matrix(): arcs, no glyph flips.
// Chapter 3's text is lensed by the black hole itself and spaghettifies into it at the horizon cut.
// Rects are read once per beat activation / resize (reads batched before writes); frames only write.
const T0 = 1.6, T1 = 3.0; // reach in tE: the full lens inside T0, nothing past T1 (a smooth finite-reach taper)
const MAXT = 2.5, PX = 0.16, PY = 0.25; // tangential stretch cap; paint padding of gradient letters (em)
const CSS = `
.gt-l{display:inline-block;pointer-events:none}
.gt-wd{white-space:nowrap}
.gt-sr{position:absolute!important;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}
.beat.gt-free .w{overflow:visible}
.gt-gi{background-image:inherit!important;background-size:0 0!important;background-repeat:no-repeat!important}
.gt-g{background-image:inherit!important;padding:${PY}em ${PX}em;margin:-${PY}em -${PX}em;-webkit-background-clip:text!important;background-clip:text!important;-webkit-text-fill-color:transparent;color:transparent;background-repeat:no-repeat}
.beat.gt-spag{opacity:var(--gt-o,1)!important;visibility:visible!important;transition:none!important}
.gt-spag .reveal,.gt-spag .w>span{opacity:1!important;transform:none!important;transition:none!important}
.gt-hint{position:fixed;left:50%;top:calc(22px + env(safe-area-inset-top,0px));z-index:45;transform:translateX(-50%);display:flex;align-items:center;gap:12px;pointer-events:none;opacity:0;transition:opacity 1.1s ease;font:500 10px/1 var(--mono,ui-monospace,monospace);letter-spacing:.24em;text-transform:uppercase;color:rgba(236,240,255,.82);white-space:nowrap;text-shadow:0 1px 10px rgba(0,0,0,.85)}
.gt-hint.on{opacity:1}
@media (max-width:760px){.gt-hint{top:calc(66px + env(safe-area-inset-top,0px))}}
.gt-hint i{position:relative;width:20px;height:20px;border-radius:50%;border:1px solid rgba(236,240,255,.7);box-shadow:0 0 12px rgba(160,190,255,.35),inset 0 0 6px rgba(160,190,255,.25)}
.gt-hint i::after{content:"";position:absolute;left:50%;top:50%;width:6px;height:6px;margin:-3px;border-radius:50%;background:#000;box-shadow:0 0 0 1px rgba(236,240,255,.35)}
`;
const sm = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const P = [0, 0], M = [1, 0, 0, 1];

// m = S m, with S = sr r r^T + st t t^T (r radial unit vector, t = r turned 90 degrees); m = [a, b, c, d] (CSS order)
function stretch(m, rx, ry, sr, st) {
  const tx = -ry, ty = rx;
  const s11 = sr * rx * rx + st * tx * tx, s12 = sr * rx * ry + st * tx * ty, s22 = sr * ry * ry + st * ty * ty;
  const a = s11 * m[0] + s12 * m[1], b = s12 * m[0] + s22 * m[1], c = s11 * m[2] + s12 * m[3], d = s12 * m[2] + s22 * m[3];
  m[0] = a; m[1] = b; m[2] = c; m[3] = d;
}
// one point lens at (lx, ly) with Einstein radius te (px) moves p to its outer image; cap > 0 softly limits the shift
function lens(p, m, lx, ly, te, cap, t0, t1) {
  if (!(te > 0.5)) return;
  let rx = p[0] - lx, ry = p[1] - ly;
  const b = Math.hypot(rx, ry);
  if (b >= t1 * te) return;
  if (b < 1e-3) { rx = 0; ry = -1; } else { rx /= b; ry /= b; }
  const w = 1 - sm(t0 * te, t1 * te, b);
  const sh0 = ((b + Math.sqrt(b * b + 4 * te * te)) / 2 - b) * w, sh = cap > 0 ? cap * Math.tanh(sh0 / cap) : sh0;
  const th = b + sh, tp = b + sh0, f = sh0 > 1e-6 ? sh / sh0 : 1; // f: the share of the physical shift kept by the cap
  const st = b < 1e-3 ? MAXT : Math.min(MAXT, th / b);
  const sr = Math.max(0.45, 1 + ((tp * tp) / (tp * tp + te * te) - 1) * w * f); // radial: dtheta/dbeta = theta^2 / (theta^2 + tE^2)
  p[0] = lx + rx * th; p[1] = ly + ry * th;
  stretch(m, rx, ry, sr, st);
}
function parseTy(el) { const s = el.style.transform || '', i = s.indexOf('translate3d('); if (i < 0) return 0; const v = parseFloat(s.slice(i + 12).split(',')[1]); return Number.isFinite(v) ? v : 0; }
// layout position of el's border box inside the beat (offsets ignore every transform: the reveal's and ours)
function offIn(el, stop) {
  let x = 0, y = 0;
  for (let e = el; e && e !== stop; e = e.offsetParent) { x += e.offsetLeft; y += e.offsetTop; }
  return [x, y];
}
// the element painting a background-clip:text fill for this text (the .hl gradient), if any
function gradHost(el, root) {
  for (let a = el; a; a = a.parentElement) {
    const cs = getComputedStyle(a);
    if ((cs.backgroundClip === 'text' || cs.webkitBackgroundClip === 'text') && cs.backgroundImage && cs.backgroundImage !== 'none') return a;
    if (a === root) break;
  }
  return null;
}

export class GravityType {
  constructor() {
    this.ready = false; this.fontsOk = false; this.kern = []; this.beats = []; this.dirty = false; this.bh = { k: 0, r: 0 }; this.hs = 0; this.t = 0;
    const st = document.createElement('style'); st.id = 'gt-style'; st.textContent = CSS; document.head.appendChild(st);
    const go = (ok) => { if (this.ready) return; this.fontsOk = ok; try { this.init(); } catch (e) { console.warn('[v5] gravity type: split failed', e); this.beats = []; } this.ready = true; };
    const fr = document.fonts && document.fonts.ready;
    Promise.race([(fr || Promise.resolve()).then(() => true), new Promise((r) => setTimeout(() => r(false), 3000))]).then((ok) => requestAnimationFrame(() => go(ok)), () => requestAnimationFrame(() => go(false)));
    addEventListener('resize', () => { this.dirty = true; }, { passive: true });
    if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', () => { if (this.ready && !this.fontsOk) { for (const el of this.kern) el.style.marginRight = ''; this.kern = []; this.fontsOk = true; } this.dirty = true; });
  }

  init() {
    const jobs = [];
    for (const c of document.querySelectorAll('.chap')) {
      const n = +c.dataset.ch, bs = Array.from(c.querySelectorAll('.beat'));
      bs.forEach((b, i) => {
        if (b.closest('.offer')) return;
        const els = Array.from(b.querySelectorAll('h1, h2, h3'));
        const spag = n === 3 && i === bs.length - 1; // the text on screen when we fall in
        if (spag) els.push(...Array.from(b.querySelectorAll('p')).filter((p) => !p.querySelector('input, textarea, select, button')));
        const todo = els.filter((e) => !e.dataset.gt);
        if (todo.length) jobs.push({ b, n, els: todo, spag });
      });
    }
    // 1 (read): each character's shaped advance, kerning included, before the split
    const T = [], lab = [], rg = document.createRange();
    for (const j of jobs) for (const el of j.els) {
      lab.push([el, el.textContent.replace(/\s+/g, ' ').trim()]);
      const tw = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let t = tw.nextNode(); t; t = tw.nextNode()) {
        if (!/\S/.test(t.nodeValue)) continue;
        const s = t.nodeValue, adv = [];
        for (let i = 0; i < s.length; i++) { if (/\s/.test(s[i])) { adv.push(0); continue; } rg.setStart(t, i); rg.setEnd(t, Math.min(s.length, i + 1)); adv.push(rg.getBoundingClientRect().width); }
        T.push({ t, j, adv, fs: parseFloat(getComputedStyle(t.parentElement).fontSize) || 16, host: gradHost(t.parentElement, el), ls: [] });
      }
    }
    // 2 (write): per-letter inline-block spans (aria-hidden; the parent keeps the words for assistive tech)
    for (const [el, text] of lab) {
      el.dataset.gt = '1';
      if (/^H\d$/.test(el.tagName)) el.setAttribute('aria-label', text);
      else { const sr = document.createElement('span'); sr.className = 'gt-sr'; sr.textContent = text; el.appendChild(sr); }
    }
    for (const x of T) {
      const s = x.t.nodeValue, frag = document.createDocumentFragment();
      let wd = null, sp = false;
      for (let i = 0; i < s.length;) {
        const n = s.codePointAt(i) > 0xffff ? 2 : 1, c = s.slice(i, i + n), a = x.adv[i];
        i += n;
        if (/\s/.test(c)) { wd = null; if (!sp) frag.appendChild(document.createTextNode(' ')); sp = true; continue; }
        sp = false;
        if (!wd) { wd = document.createElement('span'); wd.className = 'gt-wd'; frag.appendChild(wd); }
        const l = document.createElement('span');
        l.className = x.host ? 'gt-l gt-g' : 'gt-l'; l.setAttribute('aria-hidden', 'true'); l.textContent = c;
        wd.appendChild(l); x.ls.push({ el: l, adv: a });
      }
      x.t.replaceWith(frag);
      if (x.host) {
        for (const l of x.ls) for (let a = l.el.parentElement; a && a !== x.host; a = a.parentElement) a.classList.add('gt-gi');
        x.host.style.backgroundSize = '0 0'; // its letters paint their own slice of the same gradient
      }
    }
    // 3 (read) + 4 (write): put the kerning back (split letters are shaped alone)
    for (const x of T) for (const l of x.ls) l.w = l.el.getBoundingClientRect().width;
    for (const x of T) for (const l of x.ls) {
      const pad = x.host ? 2 * PX * x.fs : 0, base = x.host ? -PX : 0, mr = (l.adv - (l.w - pad)) / x.fs + base;
      if (Math.abs(mr - base) > 0.004 && Number.isFinite(mr)) { l.el.style.marginRight = `${mr.toFixed(4)}em`; this.kern.push(l.el); }
    }
    const map = new Map();
    for (const x of T) {
      let st = map.get(x.j.b);
      if (!st) { st = { el: x.j.b, ch: x.j.n, spag: x.j.spag, L: [], on: false, onT: 0, offT: -1e9, set: false, rev: 0, meas: false, applied: false, spagOn: false }; map.set(x.j.b, st); }
      for (const l of x.ls) st.L.push({ el: l.el, host: x.host, x: 0, y: 0, h: 0, tf: '', op: '', bg: '', bs: '', bp: '', pbg: '', pbs: '', pbp: '' });
    }
    this.beats = Array.from(map.values());
    // 5 (read) + 6 (write): positions and the gradient pieces for every beat
    for (const st of this.beats) this.measure(st);
    for (const st of this.beats) this.paint(st);
  }

  // reads only: letter centres relative to the beat's layout origin, and each gradient letter's slice
  measure(st) {
    const br = st.el.getBoundingClientRect(), H = new Map();
    st.bx = br.left; st.by = br.top - parseTy(st.el); st.meas = true; st.bad = !br.width && !br.height;
    for (const l of st.L) {
      const o = offIn(l.el, st.el), w = l.el.offsetWidth, h = l.el.offsetHeight;
      l.x = o[0] + w / 2; l.y = o[1] + h / 2;
      if (!l.host) continue;
      let g = H.get(l.host);
      if (!g) {
        const cs = getComputedStyle(l.host);
        let rs; // an atomic host may carry the reveal transform itself: use its layout box
        if (cs.display !== 'inline') { const o2 = offIn(l.host, st.el); rs = [{ l: o2[0], t: o2[1], w: l.host.offsetWidth, h: l.host.offsetHeight }]; }
        else {
          let sx = 0, sy = 0; // take out any reveal transform on its ancestors: layout, not motion
          for (let a = l.host.parentElement; a && a !== st.el; a = a.parentElement) {
            const t = getComputedStyle(a).transform;
            if (!t || t === 'none') continue;
            const v = t.slice(t.indexOf('(') + 1, -1).split(',').map(Number);
            if (v.length === 6) { sx += v[4]; sy += v[5]; } else if (v.length === 16) { sx += v[12]; sy += v[13]; }
          }
          rs = Array.from(l.host.getClientRects()).map((q) => ({ l: q.left - br.left - sx, t: q.top - br.top - sy, w: q.width, h: q.height }));
        }
        g = { clone: (cs.boxDecorationBreak || cs.webkitBoxDecorationBreak) === 'clone', rs, W: rs.reduce((a, q) => a + q.w, 0) };
        H.set(l.host, g);
      }
      if (!g.rs.length) continue;
      let off = 0, f = null;
      for (const q of g.rs) { if (l.y >= q.t && l.y <= q.t + q.h) { f = q; break; } off += q.w; }
      if (!f) { f = g.rs[0]; off = 0; }
      l.bs = `${(g.clone ? f.w : g.W).toFixed(1)}px ${f.h.toFixed(1)}px`;
      l.bp = `${(-((g.clone ? 0 : off) + o[0] - f.l)).toFixed(1)}px ${(-(o[1] - f.t)).toFixed(1)}px`;
    }
  }

  // one read per activation: where the beat sits now (its drift translate is re-read from style each frame)
  origin(st) { const br = st.el.getBoundingClientRect(); st.bx = br.left; st.by = br.top - parseTy(st.el); st.meas = true; }

  paint(st) {
    for (const l of st.L) {
      if (!l.host) continue;
      if (l.bs !== l.pbs) { l.el.style.backgroundSize = l.bs; l.pbs = l.bs; }
      if (l.bp !== l.pbp) { l.el.style.backgroundPosition = l.bp; l.pbp = l.bp; }
    }
  }

  stats() { let a = 0; for (const st of this.beats) for (const l of st.L) if (l.tf) a++; return { ready: this.ready, beats: this.beats.length, letters: this.beats.reduce((n, s) => n + s.L.length, 0), moved: a, bhR: +this.bh.r.toFixed(1), hint: this.hs }; }


  // per frame, from main.js: c = { S, F, inCut, cutT, lens, k, te, anchor }
  update(c) {
    if (!this.ready) return;
    const { S, F } = c, now = performance.now();
    if (this.dirty) { this.dirty = false; for (const st of this.beats) this.measure(st); for (const st of this.beats) this.paint(st); }
    // Sagittarius A* lenses its own chapter: centre anchor, thetaE = 1.5 x the photon-ring radius
    const bh = this.bh, dt = Math.min(0.1, S.dt || 0.016);
    let bhOn = false;
    if (S.lit && F.filmCh === 3 && F.offer < 0.05 && c.anchor) {
      const a = c.anchor('blackhole', 'center'), e = c.anchor('blackhole', 'photonRing');
      if (a && a.visible && Number.isFinite(a.x + a.y)) {
        bh.x = a.x * S.vw; bh.y = a.y * S.vh; bhOn = true;
        if (e && e.visible && Number.isFinite(e.x + e.y)) { const r = 1.5 * Math.hypot((e.x - a.x) * S.vw, (e.y - a.y) * S.vh); if (r > 2 && r < S.vh) bh.r = bh.r ? bh.r + (r - bh.r) * Math.min(1, dt * 6) : r; }
        if (!bh.r) bh.r = 0.12 * S.vh;
      }
    }
    bh.k = Math.max(0, Math.min(1, bh.k + (bhOn && !c.inCut ? dt / 0.6 : -dt / 0.4)));
    const spagOn = S.lit && c.inCut && F.cut === 3 && c.cutT < 0.5;
    // reads first: one rect for each beat that just appeared (or starts to fall in), before this frame's writes
    for (const st of this.beats) {
      st.now = st.el.classList.contains('on');
      st.fresh = st.now && !st.on;
      if (st.fresh || (st.spag && spagOn && !st.spagOn)) { if (st.bad) this.measure(st); else this.origin(st); }
    }
    for (const st of this.beats) {
      const on = st.now;
      if (st.fresh) { st.onT = now; if (st.hid) { this.clear(st); st.hid = false; } if (st.bad) this.paint(st); }
      st.on = on;
      let act = false;
      if (st.spag && spagOn) act = this.spag(st, c);
      else {
        if (st.spagOn) { st.el.classList.remove('gt-spag'); st.spagOn = false; if (!on) { this.clear(st, '0'); st.hid = true; } }
        if (on) act = this.bend(st, c, now);
      }
      if (!act && st.applied && !st.hid) this.clear(st);
      if (act !== st.free) { st.el.classList.toggle('gt-free', act); st.free = act; }
    }
    this.hint(c);
  }

  // pointer lens (+ the black hole's in chapter 3) on a visible beat, once its words have finished rising
  bend(st, c, now) {
    const age = (now - st.onT) / 1000, bh = this.bh; // wait for the words to finish rising out of their masks
    const kP = c.k * sm(1.8, 2.3, age), kB = st.ch === 3 ? bh.k * sm(1.5, 2.4, age) : 0;
    if (kP <= 0.001 && kB <= 0.001) return false;
    if (!st.meas) this.origin(st);
    const ox = st.bx, oy = st.by + parseTy(st.el), L = c.lens;
    const teP = kP > 0.001 ? c.te * Math.sqrt(kP) : 0, teB = kB > 0.001 ? bh.r * Math.sqrt(kB) : 0;
    for (const l of st.L) {
      const x0 = ox + l.x, y0 = oy + l.y;
      P[0] = x0; P[1] = y0; M[0] = 1; M[1] = 0; M[2] = 0; M[3] = 1;
      if (teB) lens(P, M, bh.x, bh.y, teB, 0.16 * teB, 1.8, 3.6); // the hole reaches further, its shift is capped (a gentle bow at rest; words stay whole)
      if (teP) lens(P, M, L.x, L.y, teP, 0, T0, T1);
      this.put(l, P[0] - x0, P[1] - y0, M, '');
    }
    st.applied = true;
    return true;
  }

  // the horizon cut: the chapter's last lines fall into the hole (radial stretch to 4x, tangential squeeze, fade)
  spag(st, c) {
    const { S } = c, s = Math.min(1, c.cutT / 0.5); // 0..1 over cut t 0..0.5 (the swap)
    if (!st.spagOn) { st.el.classList.add('gt-spag'); st.spagOn = true; st.hid = false; }
    const a = c.anchor && c.anchor('blackhole', 'center');
    const cx = a && a.visible && Number.isFinite(a.x + a.y) ? a.x * S.vw : S.vw / 2, cy = a && a.visible && Number.isFinite(a.x + a.y) ? a.y * S.vh : S.vh / 2;
    const ox = st.bx, oy = st.by + parseTy(st.el);
    for (const l of st.L) {
      const x0 = ox + l.x, y0 = oy + l.y;
      let rx = x0 - cx, ry = y0 - cy;
      const b = Math.hypot(rx, ry) || 1;
      rx /= b; ry /= b;
      const sl = Math.min(1, Math.max(0, s * 1.25 - 0.25 * Math.min(1, b / (0.9 * S.vh)))), e = sl * sl; // nearer letters go first
      const th = b * (1 - 0.97 * e);
      M[0] = 1; M[1] = 0; M[2] = 0; M[3] = 1;
      stretch(M, rx, ry, 1 + 3 * sl, 1 / (1 + 2.4 * sl));
      this.put(l, cx + rx * th - x0, cy + ry * th - y0, M, (1 - sm(0.45, 1, sl)).toFixed(3));
    }
    st.applied = true;
    return true;
  }

  put(l, dx, dy, m, op) {
    const id = Math.abs(dx) < 0.05 && Math.abs(dy) < 0.05 && Math.abs(m[0] - 1) < 0.002 && Math.abs(m[3] - 1) < 0.002 && Math.abs(m[1]) < 0.002;
    const tf = id ? '' : `matrix(${m[0].toFixed(3)},${m[1].toFixed(3)},${m[2].toFixed(3)},${m[3].toFixed(3)},${dx.toFixed(1)},${dy.toFixed(1)})`;
    if (tf !== l.tf) { l.el.style.transform = tf; l.tf = tf; }
    if (op === '1.000') op = '';
    if (op !== l.op) { l.el.style.opacity = op; l.op = op; }
  }

  clear(st, op = '') {
    for (const l of st.L) {
      if (l.tf) { l.el.style.transform = ''; l.tf = ''; }
      if (l.op !== op) { l.el.style.opacity = op; l.op = op; }
    }
    st.applied = false;
  }

  // 'YOUR CURSOR HAS MASS' / 'YOUR FINGER HAS MASS' after ignition; gone after the first interaction or 6 s
  hint(c) {
    const { S, F } = c;
    if (this.hs >= 3) return;
    if (!this.hel) {
      const el = document.createElement('div'), touch = matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints > 0 && !matchMedia('(pointer: fine)').matches);
      el.className = 'gt-hint'; el.setAttribute('aria-hidden', 'true');
      el.appendChild(document.createElement('i'));
      el.appendChild(document.createElement('span')).textContent = touch ? 'YOUR FINGER HAS MASS' : 'YOUR CURSOR HAS MASS';
      document.body.appendChild(el); this.hel = el;
    }
    if (this.hs === 0 && S.lit && S.revealed) { this.hs = 1; this.t = S.clock; }
    else if (this.hs === 1 && S.clock - this.t > 1.2) { this.hel.classList.add('on'); this.hs = 2; this.t = S.clock; this.tr = c.lens.travel; }
    else if (this.hs === 2 && (S.clock - this.t > 6 || c.lens.travel - this.tr > 80 || c.lens.down || F.offer > 0.3)) { this.hel.classList.remove('on'); this.hs = 3; }
  }
}
